import { useList, useGo, useDelete } from "@refinedev/core";
import { useMemo, useState } from "react";
import {
  Users,
  Plus,
  Shield,
  Edit,
  Trash2,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import type { AdminUser } from "./types";

import { useWebSocket } from "@/providers/WebSocketProvider";

/**
 * Admin Users list page with DataTable
 */
export function AdminUsersList() {
  useScrollRestoration();
  const go = useGo();
  const [page, setPage] = usePersistedState('admin-users:page', 1);
  const [search, setSearch] = usePersistedState('admin-users:search', '');
  const [sort, setSort] = usePersistedState<SortState | undefined>('admin-users:sort', { field: "createdAt", order: "desc" });
  const [deleteItems, setDeleteItems] = useState<AdminUser[]>([]);
  const [roleFilter, setRoleFilter] = usePersistedState('admin-users:roleFilter', 'all');
  const pageSize = 10;

  const { onlineAdmins } = useWebSocket();

  const { result, query } = useList<AdminUser>({
    resource: "admin-users",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters: useMemo(() => {
      const f: { field: string; operator: 'contains' | 'eq'; value: string }[] = [];
      if (search) {
        f.push({ field: 'search', operator: 'contains', value: search });
      }
      if (roleFilter !== 'all') {
        f.push({ field: 'role', operator: 'eq', value: roleFilter });
      }
      return f;
    }, [search, roleFilter]),
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });
  const deleteMutation = useDelete();

  // Merge API data with WebSocket real-time status
  const rawData = result.data ?? [];
  const data = rawData.map((user) => ({
    ...user,
    isOnline: onlineAdmins.some((admin) => admin.id === user.id),
  }));

  const total = result.total ?? 0;
  const isLoading = query.isLoading;
  const dateFormatter = new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const formatDateTime = (value: string | null) => {
    if (!value) {return "Нет данных";}
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    for (const item of deleteItems) {
      deleteMutation.mutate({
        resource: "admin-users",
        id: item.id,
      });
    }
    setDeleteItems([]);
  };

  const getRoleBadge = (user: AdminUser) => {
    const badge = (
      <Badge
        variant="outline"
        className={
          user.roleId
            ? "cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
            : undefined
        }
        onClick={(e) => {
          if (user.roleId) {
            e.stopPropagation();
            go({ to: `/roles/edit/${user.roleId}` });
          }
        }}
      >
        <Shield className="h-3 w-3 mr-1" />
        {user.roleDisplayName || "Без роли"}
      </Badge>
    );

    return badge;
  };

  const getStatusBadge = (isOnline: boolean) => (
    <Badge
      variant="outline"
      className={isOnline ? "border-emerald-500 text-emerald-600" : undefined}
    >
      {isOnline ? "Онлайн" : "Оффлайн"}
    </Badge>
  );

  const columns: Column<AdminUser>[] = [
    {
      key: "id",
      header: "ID",
      width: "60px",
      sortable: true,
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      render: (_, user) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{user.email}</span>
        </div>
      ),
    },
    {
      key: "role",
      header: "Роль",
      sortable: true,
      render: (_, user) => getRoleBadge(user),
    },
    {
      key: "isOnline",
      header: "Статус",
      render: (_, user) => getStatusBadge(user.isOnline),
    },
    {
      key: "lastActivityAt",
      header: "Последняя активность",
      sortable: true,
      render: (_, user) => formatDateTime(user.lastActivityAt),
    },
    {
      key: "createdAt",
      header: "Создан",
      sortable: true,
      render: (value) =>
        formatDateTime(typeof value === "string" ? value : null),
    },
    {
      key: "updatedAt",
      header: "Обновлен",
      sortable: true,
      render: (value) =>
        formatDateTime(typeof value === "string" ? value : null),
    },
  ];

  const bulkActions: BulkAction<AdminUser>[] = [
    {
      label: "Редактировать",
      icon: <Edit className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/admin-users/edit/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Посмотреть логи",
      icon: <History className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/audit-logs?adminId=${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Администраторы"
        description="Управление доступом к панели"
        actions={
          <Button onClick={() => go({ to: "/admin-users/create" })}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить
          </Button>
        }
        sticky
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <TableToolbar
            searchValue={search}
            onSearchChange={(v) => { setSearch(v); setPage(1); }}
            searchPlaceholder="Поиск по email..."
          />
        </div>
        <Select
          value={roleFilter}
          onValueChange={(value) => {
            setRoleFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Роль" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все роли</SelectItem>
            <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
            <SelectItem value="ADMIN">Admin</SelectItem>
            <SelectItem value="MANAGER">Manager</SelectItem>
            <SelectItem value="SUPPORT">Support</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(user) => go({ to: `/admin-users/edit/${user.id}` })}
        emptyMessage="Нет администраторов"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? "Удалить администратора?"
                : `Удалить ${deleteItems.length} администраторов?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы собираетесь удалить {deleteItems[0].email}. Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы собираетесь удалить {deleteItems.length} администраторов. Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
